
import React, { useMemo } from 'react';
import { AnalysisResult } from '../../types';
import { determineRecommendedLevel, GLOBAL_SEVERITY_MATRIX } from '../../services/severityEngine';
import { useGuitarMatch } from '../../context/GuitarMatchContext';

interface CalibrationResultProps {
    result: AnalysisResult;
    onAccept: () => void;
}

export const CalibrationResult: React.FC<CalibrationResultProps> = ({ result, onAccept }) => {
    const { setCoachLevel } = useGuitarMatch();
    const metrics = result.session.metrics;
    
    // Calcul du niveau recommandé en direct basé sur les métriques brutes
    const recommendation = useMemo(() => {
        return determineRecommendedLevel(metrics, result.session.bpm);
    }, [metrics, result.session.bpm]);

    const handleAccept = () => {
        setCoachLevel(recommendation.level);
        onAccept();
    };

    const levelData = GLOBAL_SEVERITY_MATRIX[recommendation.level];

    return (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 md:p-12 animate-fade-in bg-[#020506]">
            <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                
                {/* PARTIE GAUCHE : LEVEL CARD */}
                <div className="flex flex-col items-center text-center space-y-8 order-2 lg:order-1">
                    <div className="relative group">
                        <div className="absolute inset-0 bg-white/5 rounded-full blur-3xl group-hover:bg-cyan-500/10 transition-colors duration-1000"></div>
                        <div className="relative w-64 h-64 rounded-full border-4 border-white/10 bg-black flex flex-col items-center justify-center shadow-2xl overflow-hidden">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.4em] mb-2">Calibration</span>
                            <span className="text-8xl font-black italic text-white tracking-tighter leading-none">L{recommendation.level}</span>
                            <span className="text-xl font-bold text-cyan-400 uppercase tracking-widest mt-2">{levelData.label}</span>
                        </div>
                    </div>

                    <div className="space-y-4 max-w-md">
                        <div className="bg-white/5 p-6 rounded-3xl border border-white/5 text-left">
                            <h3 className="text-[10px] font-black text-cyan-400 uppercase tracking-widest mb-3">Diagnostic IA</h3>
                            <p className="text-sm text-white font-medium italic leading-relaxed">
                                "{recommendation.details}"
                            </p>
                        </div>
                        <button 
                            onClick={handleAccept}
                            className="w-full py-6 rounded-full bg-white text-black font-black uppercase text-xs tracking-[0.3em] hover:scale-105 hover:bg-cyan-400 transition-all shadow-[0_0_40px_rgba(6,182,212,0.3)]"
                        >
                            ACCEPTER LE NIVEAU {recommendation.level}
                        </button>
                    </div>
                </div>

                {/* PARTIE DROITE : METRICS PROOF */}
                <div className="bg-black/40 border border-white/10 rounded-[48px] p-10 h-full flex flex-col justify-center gap-8 order-1 lg:order-2 shadow-2xl">
                    <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter">Votre <span className="text-slate-500">Empreinte</span></h3>
                    
                    <div className="space-y-6">
                        {/* RYTHME */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-end">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Stabilité Rythmique</span>
                                <span className="text-lg font-black text-white italic">{Math.round(metrics.timingErrorMs || 0)}ms</span>
                            </div>
                            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                                {/* Barre inverse : plus c'est grand, plus c'est rouge */}
                                <div 
                                    className={`h-full rounded-full ${Math.abs(metrics.timingErrorMs || 0) < 50 ? 'bg-emerald-500' : 'bg-orange-500'}`} 
                                    style={{ width: `${Math.min(100, (300 - Math.abs(metrics.timingErrorMs || 0)) / 3)}%` }} 
                                />
                            </div>
                        </div>

                        {/* HARMONIE */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-end">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Clarté Harmonique</span>
                                <span className="text-lg font-black text-white italic">{metrics.harmony}%</span>
                            </div>
                            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-cyan-500 rounded-full" 
                                    style={{ width: `${metrics.harmony}%` }} 
                                />
                            </div>
                        </div>

                        {/* STABILITÉ */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-end">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Stabilité (Jitter)</span>
                                <span className="text-lg font-black text-white italic">{((metrics.debug?.stability || 0) * 100).toFixed(0)}%</span>
                            </div>
                            <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-purple-500 rounded-full" 
                                    style={{ width: `${(metrics.debug?.stability || 0) * 100}%` }} 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-white/5">
                        <p className="text-[9px] text-slate-500 uppercase font-bold italic leading-relaxed">
                            Le niveau de sévérité détermine la tolérance de l'IA aux micro-erreurs. En L{recommendation.level}, vous serez jugé avec une tolérance de ±{levelData.timing.toleranceMsBase}ms.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
